import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElementWithChildren } from "backend/model/model";
import React from "react";
import { StrategyCard } from "../cards/StrategyCard";
import StrategyMapRowName from "../texts/StrategyMapRowName";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import ThemeTitle from "../texts/ThemeTitle";

const useStrategyTableWithThemesStyles = makeThemedStyles(
  "StrategyTableWithThemes",
  (theme) => ({
    root: {
      display: "flex",
      width: "100%",
      flexDirection: "column",
      gap: "20px",
    },
    row: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "nowrap",
      gap: "20px",
    },
    leftTitle: {
      width: "150px",
      minWidth: "150px",
    },
    cell: {
      display: "flex",
      flexDirection: "row",
    },
  })
);

type ThemeConfig = {
  color: string;
  textColor: string;
  disabled?: boolean;
  order?: number;
};

const parseThemeConfig = (description?: string): ThemeConfig => {
  try {
    return JSON.parse(description || "{}") as ThemeConfig;
  } catch {
    return { color: "", textColor: "", disabled: false, order: undefined };
  }
};

const computeCellWidthPx = (
  sameLevelCount: number,
  isDisabled: boolean,
  activeThemeCount: number
): string | undefined => {
  if (isDisabled) {
    const total = activeThemeCount * 300 - activeThemeCount * 10; // total width minus gaps
    return `${total / Math.max(1, sameLevelCount)}px`;
  }
  switch (sameLevelCount) {
    case 1:
      return "300px";
    case 2:
      return "140px"; // Fits 2 in 300px space
    case 3:
      return "87px"; // Fits 3 in 300px space
    case 4:
      return "60px"; // Fits 4 in 300px space
    case 5:
      return "44px"; // Fits 5 in 300px space
    default:
      return undefined;
  }
};

const StrategyGridWithThemes = (props: {
  elements: ICardElementWithChildren[];
  elementBgImages?: string[];
  primaryThemeColors?: string[];
  primaryThemeTextColors?: string[];
  defaultThemeColor?: string;
  themes: ICardElementWithChildren[];
  onSelect?: (el: ICardElementWithChildren) => void;
}) => {
  const {
    elements: data,
    themes,
    primaryThemeColors,
    primaryThemeTextColors,
    defaultThemeColor,
    elementBgImages,
    onSelect,
  } = props;

  const styles = useStrategyTableWithThemesStyles();

  const themeIndexByObjective = React.useMemo(() => {
    const result: Record<
      string,
      {
        index: number;
        color: string;
        textColor: string;
        disabled?: boolean;
        order?: number;
      }
    > = {};

    const list = flattenHierarchy(data);
    if (!list || !themes) return result;

    list
      .filter((el) => el.typename === "Strategic Objective")
      .forEach((strategyObjective) => {
        const parts = strategyObjective.symbol.split("_");
        const themeCode = parts[1];

        const themeMatch = themes.find((th) => th.symbol === themeCode);
        if (!themeMatch) return;

        const idx = themes.indexOf(themeMatch);
        const cfg = parseThemeConfig(themeMatch.description);

        result[strategyObjective.id] = {
          index: idx,
          color: cfg.color || "",
          textColor: cfg.textColor || "",
          disabled: !!cfg.disabled,
          order: cfg.order,
        };
      });

    return result;
  }, [data, themes]);

  const { visibleThemes, disabledThemes } = React.useMemo(() => {
    if (!themes) return { visibleThemes: [], disabledThemes: [] };

    const indexMeta: Record<number, { disabled?: boolean; order?: number }> =
      {};

    for (const v of Object.values(themeIndexByObjective)) {
      if (v && v.index !== undefined) {
        const prev = indexMeta[v.index];
        if (!prev) {
          indexMeta[v.index] = { disabled: v.disabled, order: v.order };
        } else {
          indexMeta[v.index] = {
            disabled: prev.disabled && v.disabled,
            order: prev.order ?? v.order,
          };
        }
      }
    }

    const processedThemes = themes.map((t, idx) => {
      const meta = indexMeta[idx];
      return {
        theme: t,
        order: meta?.order ?? Number.POSITIVE_INFINITY,
        disabled: meta?.disabled ?? false,
        hasData: !!meta,
      };
    });

    processedThemes.sort((a, b) => a.order - b.order);

    return {
      visibleThemes: processedThemes
        .filter((x) => !x.disabled && x.hasData)
        .map((x) => x.theme),
      disabledThemes: processedThemes
        .filter((x) => x.disabled && x.hasData)
        .map((x) => x.theme),
    };
  }, [themes, themeIndexByObjective]);

  const activeThemeCount = visibleThemes.length;

  return (
    <>
      <div className={styles.root} key={`StrategyTable${data?.[0]?.id || ""}`}>
        <div className={styles.row} key="Themes">
          <div className={styles.leftTitle} />
          {themes
            ? visibleThemes.map((t) => {
                const themeIndex = themes.indexOf(t);
                const cfg = parseThemeConfig(t.description);
                const bg =
                  cfg.color ?? primaryThemeColors?.[themeIndex] ?? undefined;
                const txt =
                  cfg.textColor ??
                  primaryThemeTextColors?.[themeIndex] ??
                  undefined;
                return (
                  <div
                    className={styles.cell}
                    key={t.id}
                    style={{ width: "300px" }}
                  >
                    <ThemeTitle textColor={txt} background={bg}>
                      {t.name}
                    </ThemeTitle>
                  </div>
                );
              })
            : null}
        </div>

        {data?.map((rowEl, rowIdx: number) => {
          const rowChildren = (rowEl.children ?? [])
            .slice()
            .sort((a, b) => a.symbol.localeCompare(b.symbol));

          const byThemeIndex = new Map<number, ICardElementWithChildren[]>();
          for (const ch of rowChildren) {
            const info = themeIndexByObjective[ch.id];
            if (info?.index !== undefined) {
              const arr = byThemeIndex.get(info.index) ?? [];
              arr.push(ch);
              byThemeIndex.set(info.index, arr);
            }
          }

          const hasDisabledItemsInRow = disabledThemes.some((dt) => {
            const idx = themes.indexOf(dt);
            return (byThemeIndex.get(idx)?.length || 0) > 0;
          });

          const renderThemeGroup = (
            t: ICardElementWithChildren,
            isThemeDisabled: boolean
          ) => {
            const themeIndex = themes.indexOf(t);
            const childrenForTheme = byThemeIndex.get(themeIndex) ?? [];
            const sameLevelCount = childrenForTheme.length;

            if (sameLevelCount === 0) {
              // disabled themes don't need spacers
              if (!isThemeDisabled && !hasDisabledItemsInRow) {
                return (
                  <div
                    className={styles.cell}
                    key={`${rowEl.id}-spacer-${themeIndex}`}
                    style={{
                      width: computeCellWidthPx(1, false, activeThemeCount),
                    }}
                  />
                );
              }
              return null;
            }

            return childrenForTheme.map((ch) => {
              const info = themeIndexByObjective[ch.id];
              const primaryColor =
                info?.color ||
                (info?.index !== undefined
                  ? props.primaryThemeColors?.[info.index]
                  : undefined) ||
                props.defaultThemeColor;
              const textColor =
                info?.textColor ||
                (info?.index !== undefined
                  ? props.primaryThemeTextColors?.[info.index]
                  : undefined) ||
                "#fff";

              const cellWidth = computeCellWidthPx(
                sameLevelCount,
                isThemeDisabled,
                activeThemeCount
              );

              return (
                <div
                  className={styles.cell}
                  key={ch.id}
                  style={{ width: cellWidth }}
                >
                  <StrategyCard
                    primaryColor={primaryColor}
                    textColor={textColor}
                    width={cellWidth}
                    backgroundImage={props.elementBgImages?.[rowIdx]}
                    typenames={[
                      { name: "KPI", title: "KPIs" },
                      { name: "Initiative", title: "Initiatives" },
                    ]}
                    element={ch}
                    onSelect={() => props.onSelect?.(ch)}
                  />
                </div>
              );
            });
          };

          return (
            <div className={styles.row} key={rowEl.id}>
              <div className={styles.leftTitle} key={`Heading ${rowEl.id}`}>
                <StrategyMapRowName>{rowEl.name}</StrategyMapRowName>
              </div>

              {visibleThemes.map((t) => renderThemeGroup(t, false))}
              {disabledThemes.map((t) => renderThemeGroup(t, true))}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default StrategyGridWithThemes;
